package com.siriusxm.pia.views.unifiedaggregator.types

import androidx.compose.runtime.*
import com.siriusxm.pia.components.box
import com.siriusxm.pia.components.boxMessage
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.events.*
import unifiedcontent.aggregator.CloudEventList
import unifiedcontent.aggregator.EntityTypeConfiguration
import unifiedcontent.aggregator.EventQueryOption
import contentingestion.unifiedmodel.EntityType
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.Text

/**
 * A view for events for a given entity type.
 *
 * **Controlled**, same shape as [ProducerValidEvents] (UCA-1028)/(UCA-1039): holds no filter state
 * of its own, and every filter change is handed to [onFiltersChange]. That is what lets the entity
 * type detail page keep the selections in the URL.
 *
 * @param filters the current filter selections. [EventFilters.range] and [EventFilters.queryType]
 *   both drive the server query; [EventFilters.eventType] filters the loaded events client-side.
 * @param onFiltersChange called with the new selections whenever the user changes a filter.
 */
@Composable
fun EntityTypeEvents(
    aggregator: AggregatorService,
    entityType: EntityTypeConfiguration,
    filters: EventFilters,
    onFiltersChange: (EventFilters) -> Unit
) {
    var loading by remember { mutableStateOf(false) }
    var events by remember { mutableStateOf<CloudEventList?>(null) }
    var failureMessage by remember { mutableStateOf<String?>(null) }

    val eventsPeriod = filters.range
    // Defaults to Output for a link that predates this filter or names no direction, matching the
    // view's previous default before filters lived in the URL.
    val queryType = filters.queryType ?: EventQueryOption.Output
    val selectedEventType = filters.eventType

    Style(EntityEventsStyles)

    LaunchedEffect(entityType, eventsPeriod, queryType) {
        val range = eventsPeriod?.range()
        if (range == null) {
            loading = false
            events = null
            failureMessage = null
            return@LaunchedEffect
        }
        if (eventsPeriod?.isOutsideEventRetention() == true) {
            // A range from a link or bookmark can predate retention; don't send a search the
            // backend can only reject (UCA-1168).
            loading = false
            events = null
            failureMessage = EVENT_RETENTION_MESSAGE
            return@LaunchedEffect
        }

        failureMessage = null
        loading = true
        events = null
        try {
            loadAccumulatedEvents({ queryId ->
                if (queryId != null) {
                    aggregator.clientApi.getEntityTypeEvents(
                        entityType = EntityType.fromName(entityType.type),
                        queryId = queryId
                    )
                } else {
                    aggregator.clientApi.getEntityTypeEvents(
                        entityType = EntityType.fromName(entityType.type),
                        start = range.start,
                        end = range.endInclusive,
                        options = listOf(queryType),
                        limit = 100
                    )
                }
            }) {
                events = it
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            failureMessage = e.message ?: "The event search failed for an unknown reason."
        } finally {
            loading = false
        }
    }

    eventsSelectionHeader(
        loading, selectedTimeRange = eventsPeriod, selectedQueryOption = queryType,
        showQueryOptions = true,
        selectedEventType = selectedEventType,
        showEventTypeFilter = true
    ) { range, option, eventType ->
        // A different query direction invalidates the event-type selection: a direction change is
        // detected against `queryType` (the defaulted value actually shown), not `filters.queryType`
        // directly, so a link that names no direction at all doesn't read as "changed" the moment it
        // first renders with the Output default. Clearing the range invalidates it too — the event
        // type filter otherwise stays set (but invisible, since the table shows "select a time
        // period" instead) and silently reapplies to whatever range the user picks next.
        val queryChanged = option != null && option != queryType
        val filterInvalidated = queryChanged || range == null
        onFiltersChange(
            filters.copy(
                range = range,
                queryType = option ?: filters.queryType,
                eventType = if (filterInvalidated) null else eventType
            )
        )
    }

    box({
        paddedContent = false
    }) {
        if (failureMessage != null) {
            boxMessage(failureMessage!!)
        } else if (eventsPeriod == null) {
            boxMessage("Please select a time period.")
        }

        val resultEvents = if (eventsPeriod != null) events.orEmpty() else emptyList()
        val filteredEvents = resultEvents.filterByEventTypes(selectedEventType.asFilterSet())

        if (resultEvents.isNotEmpty()) {
            if (filteredEvents.isNotEmpty()) {
                cloudEventTable(
                    aggregator,
                    filteredEvents,
                    extraConfig = {
                        if (queryType == EventQueryOption.Output) {
                            column("Sequence") {
                                content { event ->
                                    event.sequence?.let { Text(it.trimStart('0').ifEmpty { "0" }) }
                                }
                            }
                        }
                        column("Entity") {
                            content { event ->
                                event.displayEntityId()?.let { id -> EntityLink(id) }
                            }
                        }
                    }
                )
            } else if (selectedEventType != null) {
                boxMessage("No events match the selected event types.")
            }
        } else {
            if (!loading && eventsPeriod != null && failureMessage == null) {
                boxMessage("No ${entityType.name} events were published in this period")
            }
        }
    }
}
